import type { SubWalletResponse } from "@developerz.ai/ggcoin-sdk";
import { createSignal, For, onMount, Show } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { Money } from "../components/Money.tsx";
import { parseAmountInput, sanitizeAmountInput } from "../lib/format.ts";

/**
 * Create + list sub-wallets, both server-backed. The master wallet is resolved
 * once via the shared cache; the list is the authoritative
 * `GET /wallets/:walletId/sub-wallets` so Buy/Send see the same thing.
 */
export function SubWallets() {
  const { client, store, ensureMaster } = useAuth();
  const [walletId, setWalletId] = createSignal<string | null>(null);
  const [subs, setSubs] = createSignal<SubWalletResponse[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<unknown | null>(null);

  const [kind, setKind] = createSignal<"human" | "agent">("human");
  const [label, setLabel] = createSignal("");
  const [spendCap, setSpendCap] = createSignal("");
  const [approvalThreshold, setApprovalThreshold] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  // Agent-token minting: which sub-wallet is in flight, and the token shown once.
  const [minting, setMinting] = createSignal<string | null>(null);
  const [minted, setMinted] = createSignal<{ subWalletId: string; token: string } | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const master = await ensureMaster();
      setWalletId(master.id);
      const list = await client.listSubWallets(master.id);
      setSubs(list);
      for (const s of list) store.rememberSubWallet(s.id);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  onMount(() => {
    void load();
  });

  async function onCreate(e: Event) {
    e.preventDefault();
    setError(null);
    const capWire = parseAmountInput(spendCap());
    const thresholdWire = parseAmountInput(approvalThreshold());
    if (!capWire || !thresholdWire) {
      setError(
        new Error("Spend cap and approval threshold must be positive amounts (≤ 2 decimals)."),
      );
      return;
    }
    const wid = walletId();
    if (!wid) {
      setError(new Error("Your master wallet isn't loaded yet — try again in a moment."));
      return;
    }
    setBusy(true);
    try {
      await client.createSubWallet({
        walletId: wid,
        kind: kind(),
        label: label().trim(),
        spendCap: capWire,
        approvalThreshold: thresholdWire,
      });
      setLabel("");
      setSpendCap("");
      setApprovalThreshold("");
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function mintToken(subWalletId: string) {
    // Guard: don't mint while one is in flight, or while a freshly-minted token
    // is still on screen un-acknowledged — a second mint would overwrite the
    // one-time secret (orphaning a live, uncopyable token that still counts
    // against the per-sub-wallet cap). The user must "Done" the current one first.
    if (minting() !== null || minted() !== null) return;
    setMinting(subWalletId);
    setError(null);
    try {
      const res = await client.mintAgentToken({ subWalletId });
      setMinted({ subWalletId, token: res.token });
    } catch (err) {
      setError(err);
    } finally {
      setMinting(null);
    }
  }

  return (
    <>
      <section class="card">
        <h2>Create a sub-wallet</h2>
        <ErrorBanner error={error()} />
        <form onSubmit={onCreate}>
          <label for="kind">Kind</label>
          <select
            id="kind"
            value={kind()}
            onChange={(e) => setKind(e.currentTarget.value as "human" | "agent")}
          >
            <option value="human">human</option>
            <option value="agent">agent</option>
          </select>
          <label for="label" style={{ "margin-top": "0.75rem" }}>
            Label
          </label>
          <input
            id="label"
            value={label()}
            onInput={(e) => setLabel(e.currentTarget.value)}
            placeholder="bot-A"
            required
          />
          <div class="row">
            <div>
              <label for="cap">Spend cap (GG)</label>
              <input
                id="cap"
                inputmode="decimal"
                value={spendCap()}
                onInput={(e) => setSpendCap(sanitizeAmountInput(e.currentTarget.value))}
                placeholder="100.00"
                required
              />
            </div>
            <div>
              <label for="threshold">Approval threshold (GG)</label>
              <input
                id="threshold"
                inputmode="decimal"
                value={approvalThreshold()}
                onInput={(e) => setApprovalThreshold(sanitizeAmountInput(e.currentTarget.value))}
                placeholder="25.00"
                required
              />
            </div>
          </div>
          <div class="row" style={{ "margin-top": "0.75rem" }}>
            <button type="submit" class="primary" disabled={busy() || !walletId()}>
              {busy() ? "Creating…" : "Create sub-wallet"}
            </button>
          </div>
        </form>
      </section>

      <section class="card">
        <h2>Your sub-wallets</h2>
        <Show
          when={!loading()}
          fallback={
            <p class="muted" style={{ "margin-top": 0 }}>
              Loading…
            </p>
          }
        >
          <Show
            when={subs().length > 0}
            fallback={
              <p class="muted" style={{ "margin-top": 0 }}>
                No sub-wallets yet — create one above to start receiving and spending coin.
              </p>
            }
          >
            <div class="table-scroll">
              <table class="table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Kind</th>
                    <th>Balance</th>
                    <th>Cap</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  <For each={subs()}>
                    {(sw) => (
                      <tr>
                        <td>
                          {sw.label}
                          <div class="id-cell">{sw.id}</div>
                        </td>
                        <td>{sw.kind}</td>
                        <td class="num">
                          <Money amount={sw.balance} />
                        </td>
                        <td class="num">
                          <Money amount={sw.spendCap} />
                        </td>
                        <td>
                          <span class={`badge badge-${sw.status}`}>{sw.status}</span>
                        </td>
                        <td>
                          <div class="row-actions">
                            <button
                              type="button"
                              class="secondary small"
                              disabled={minting() !== null || minted() !== null}
                              onClick={() => mintToken(sw.id)}
                              title={
                                minted() !== null
                                  ? "Copy and dismiss the current token before minting another"
                                  : "Mint an MCP agent token bound to this sub-wallet"
                              }
                            >
                              {minting() === sw.id ? "…" : "Mint token"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </Show>
        <Show when={minted()}>
          {(m) => (
            <div class="callout" role="status" style={{ "margin-top": "1rem" }}>
              <strong>Agent token for {m().subWalletId} — copy it now, shown once.</strong>
              <pre class="totp-uri">{m().token}</pre>
              <p class="muted" style={{ "margin-top": 0 }}>
                Hand this to the agent; it's scoped to this sub-wallet only. A leak exposes nothing
                else.
              </p>
              <button type="button" class="secondary" onClick={() => setMinted(null)}>
                Done
              </button>
            </div>
          )}
        </Show>
      </section>
    </>
  );
}
